import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView as gaTrackPageView, trackEvent, ConversionEvents } from '@/lib/analytics';
import { trackPageView as pixelTrackPageView } from '@/lib/metaPixel';
import { fireOncePerSession, classifyReferrer, getSessionStartMs, getSessionFlags } from '@/lib/funnel';
import { supabase } from '@/integrations/supabase/client';

/**
 * Fires PageView on every client-side route change for three sinks:
 *   - GA4 (Google Analytics) — config call with page_path
 *   - Meta Pixel (Facebook/Instagram) — track('PageView')
 *   - funnel_events — PAGE_VIEWED, plus once-per-session boot events:
 *       LANDING_VIEWED, REFERRER_CLASSIFIED, RETURNING_SESSION,
 *       and SESSION_ENDED on visibilitychange → hidden.
 *
 * Mounted inside RootLayout so it lives for the entire SPA session.
 * SSG-safe: useLocation returns the static route during prerender, but
 * tracker calls short-circuit when window is undefined.
 *
 * Consent gating: GA4 + Pixel honour their own consent state. trackEvent
 * gates funnel_events on either auth session or cookie consent, so anon
 * users without consent contribute nothing here.
 */
export default function RouteTracker(): null {
  const location = useLocation();

  // Route-change effect: fires on EVERY navigation.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const path = location.pathname + location.search;
    gaTrackPageView(path);
    pixelTrackPageView();
    trackEvent(ConversionEvents.PAGE_VIEWED, {
      path: location.pathname,
      ...(location.search && { search: location.search.slice(0, 200) }),
    });
  }, [location.pathname, location.search]);

  // Boot effect: fires once per browser session for landing/referrer/return.
  // Separate from the per-route effect so it doesn't re-fire on SPA nav.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Landing — the first route entered this session.
    if (fireOncePerSession('landing_viewed')) {
      trackEvent(ConversionEvents.LANDING_VIEWED, {
        path: location.pathname,
        ...(document.referrer && { referrer: document.referrer.slice(0, 200) }),
      });
      // Persist the first-touch landing path + referrer so Auth.signUp can
      // attach them to user_metadata. Without this, server-side signup
      // trigger (which fires on auth.users INSERT) has no client context
      // and 93% of signups end up with `path='/auth/server-trigger'` — the
      // attribution gap surfaced in the 2026-05-21 SEO audit.
      try {
        const params = new URLSearchParams(window.location.search);
        const utm: Record<string, string> = {};
        for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid']) {
          const v = params.get(k);
          if (v) utm[k] = v.slice(0, 120);
        }
        localStorage.setItem('funnel_landing_path', location.pathname.slice(0, 200));
        if (document.referrer) {
          localStorage.setItem('funnel_landing_referrer', document.referrer.slice(0, 200));
        }
        if (Object.keys(utm).length > 0) {
          localStorage.setItem('funnel_landing_utm', JSON.stringify(utm));
        }
      } catch { /* localStorage unavailable */ }
    }

    // Referrer classification — coarse bucket so we can group sessions by
    // acquisition source without joining raw referrer URLs in queries.
    if (fireOncePerSession('referrer_classified')) {
      const ref = classifyReferrer();
      trackEvent(ConversionEvents.REFERRER_CLASSIFIED, {
        source: ref.source,
        ...(ref.utm_source && { utm_source: ref.utm_source.slice(0, 80) }),
        ...(ref.utm_medium && { utm_medium: ref.utm_medium.slice(0, 80) }),
        ...(ref.utm_campaign && { utm_campaign: ref.utm_campaign.slice(0, 80) }),
      });
    }

    // Returning session — fires once per session for users whose
    // signup_complete is >7 days ago. Heuristic retention without waiting
    // for the cohort view to mature.
    // Async IIFE because supabase.auth.getUser is async; we don't gate
    // anything else on its result so it's fire-and-forget.
    if (fireOncePerSession('returning_session_checked')) {
      void (async () => {
        try {
          const { data } = await supabase.auth.getUser();
          if (!data.user?.created_at) return;
          const ageDays = (Date.now() - new Date(data.user.created_at).getTime()) / 86400_000;
          if (ageDays >= 7) {
            trackEvent(ConversionEvents.RETURNING_SESSION, {
              account_age_days: Math.round(ageDays),
            });
          }
        } catch {
          // Auth not available or network glitch — skip; we'll try next session.
        }
      })();
    }

    // session_ended on tab hide / pagehide. visibilitychange covers tab
    // switching and backgrounding; pagehide is the Safari/iOS-friendly
    // "you're closing now" signal. Both call the same handler; the
    // once-per-session guard prevents double counts.
    const flushSessionEnded = () => {
      if (document.visibilityState !== 'hidden') return;
      if (!fireOncePerSession('session_ended')) return;
      const startedAt = getSessionStartMs();
      const durationMs = startedAt ? Date.now() - startedAt : null;
      const flags = getSessionFlags();
      trackEvent(ConversionEvents.SESSION_ENDED, {
        ...(durationMs !== null && { duration_ms: durationMs }),
        path: window.location.pathname,
        // Lets analytics split bounce vs designer-abandonment without
        // joining session_ended to designer_opened by session_id at SQL
        // layer. True iff useEffect in CCTVDesignerV2 fired this session.
        had_designer_open: !!flags.designer_opened,
      });
    };
    document.addEventListener('visibilitychange', flushSessionEnded);
    window.addEventListener('pagehide', flushSessionEnded);
    return () => {
      document.removeEventListener('visibilitychange', flushSessionEnded);
      window.removeEventListener('pagehide', flushSessionEnded);
    };
    // Intentional: boot effect is mount-only — runs once per RouteTracker
    // mount (i.e. once per SPA session). eslint-disable-next-line wanted
    // because the lint rule can't tell that location.pathname here is just
    // used to label the boot event, not to subscribe to it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
